import { IconPlus, IconX } from "@tabler/icons-react";
import { Button, IconButton, Input, Select, Text } from "@/components/ui";
import { cn } from "@/lib/cn";
import type { Condicion, OperadorComparacion, ReglaDeCondicion, VariableDeEvento } from "../../../services/notificaciones-service";
import { ayudaValor, ETIQUETA_OPERADOR, OPERADORES_POR_TIPO, operadorSinValor } from "../../../utils/etiquetas";

/**
 * Condición de la conexión: reglas `campo operador valor` unidas con Y/O. Los operadores se
 * limitan al tipo de la variable (mismo criterio que el backend al publicar). Los subgrupos
 * anidados (si los hay) se conservan tal cual; en esta fase no se editan desde la UI.
 */
export function CondicionEditor({
  condicion, variables, onCambio,
}: { condicion: Condicion | null; variables: VariableDeEvento[]; onCambio: (c: Condicion | null) => void }) {
  const actual: Condicion = condicion ?? { operador: "Y", reglas: [], grupos: null };
  const tipos = new Map(variables.map((v) => [v.nombre, v.tipo]));
  const emitir = (c: Condicion) => onCambio(c.reglas.length === 0 && !(c.grupos?.length) ? null : c);
  const cambiarRegla = (i: number, cambio: Partial<ReglaDeCondicion>) =>
    emitir({ ...actual, reglas: actual.reglas.map((r, j) => (j === i ? { ...r, ...cambio } : r)) });

  const agregar = () => {
    const primera = variables[0];
    if (!primera) return;
    emitir({ ...actual, reglas: [...actual.reglas, { campo: primera.nombre, operador: OPERADORES_POR_TIPO[primera.tipo][0], valor: "" }] });
  };

  return (
    <div className="space-y-2">
      {actual.reglas.length === 0 ? (
        <Text size="xs" c="dimmed">Sin condición: se envía siempre que ocurra el evento.</Text>
      ) : (
        <div className="flex items-center gap-1 text-xs">
          <span className="text-muted-foreground">Enviar si se cumple</span>
          {(["Y", "O"] as const).map((op) => (
            <button
              key={op}
              type="button"
              onClick={() => emitir({ ...actual, operador: op })}
              className={cn("rounded border px-2 py-0.5", actual.operador === op ? "border-multiva-600 bg-multiva-600 text-white" : "border-border")}
            >
              {op === "Y" ? "todas" : "alguna"}
            </button>
          ))}
        </div>
      )}
      {actual.reglas.map((r, i) => {
        const tipo = tipos.get(r.campo);
        const operadores = tipo ? OPERADORES_POR_TIPO[tipo] : (Object.keys(ETIQUETA_OPERADOR) as OperadorComparacion[]);
        return (
          <div key={i} className="space-y-1.5 rounded-md border border-border p-2">
            <div className="flex items-center gap-1.5">
              <Select
                size="xs"
                searchable
                wrapperClassName="flex-1"
                value={r.campo}
                onValueChange={(campo) => {
                  const t = tipos.get(campo);
                  const op = t && !OPERADORES_POR_TIPO[t].includes(r.operador) ? OPERADORES_POR_TIPO[t][0] : r.operador;
                  cambiarRegla(i, { campo, operador: op });
                }}
                options={variables.map((v) => ({ value: v.nombre, label: `${v.nombre} · ${v.tipo}` }))}
              />
              <IconButton size="xs" aria-label="Quitar regla" onClick={() => emitir({ ...actual, reglas: actual.reglas.filter((_, j) => j !== i) })}>
                <IconX />
              </IconButton>
            </div>
            {!tipo ? <Text size="xs" className="text-peligro-600">La variable ya no existe en el evento.</Text> : null}
            <Select
              size="xs"
              value={r.operador}
              onValueChange={(v) => cambiarRegla(i, { operador: v as OperadorComparacion, valor: operadorSinValor(v as OperadorComparacion) ? null : r.valor ?? "" })}
              options={operadores.map((o) => ({ value: o, label: ETIQUETA_OPERADOR[o] }))}
            />
            {!operadorSinValor(r.operador) ? (
              tipo === "Booleano" ? (
                <Select size="xs" value={r.valor ?? ""} onValueChange={(v) => cambiarRegla(i, { valor: v })} options={[{ value: "true", label: "Sí" }, { value: "false", label: "No" }]} placeholder="Elige" />
              ) : (
                <Input size="xs" placeholder={tipo ? ayudaValor(tipo, r.operador) : "Valor"} value={r.valor ?? ""} onChange={(e) => cambiarRegla(i, { valor: e.target.value })} />
              )
            ) : null}
          </div>
        );
      })}
      {actual.grupos?.length ? <Text size="xs" c="dimmed">+ {actual.grupos.length} subgrupo(s) anidado(s) (se conservan).</Text> : null}
      <Button size="xs" variant="default" leftSection={<IconPlus size={12} />} onClick={agregar} disabled={variables.length === 0}>
        Agregar regla
      </Button>
    </div>
  );
}
